import { Formik } from "formik";
import React from "react";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { removeAttachmentAPI, uploadAttachmentEndpoint } from "../../../api/medicalRecord";
import { DocumentViewer } from "../../../components/CustomControls/DocViewer/DocumentViewer";
import { ExpandableContent } from "../../../components/CustomControls/ExpandableContent";
import { FilesForm } from "../../../components/CustomControls/Files/Files";
import { useFiles } from "../../../components/CustomControls/Files/Hooks";
import { uploadStatusEnum } from "../../../components/CustomControls/Files/Utils";
import { Attachments } from "../../../components/MedicalRecords/Attachments";
import { ModalSpinner, Spinner } from "../../../components/Utils/Loaders";
import { useAttachments } from "../Hooks/useAttachments";
import { ModalConfirm } from "../../../components/Modals/ModalConfirm";
import { CreateAuditModal } from "../../../components/AuditTrail/AuditModal";
import { HandleApiError } from "../../../components/Notifications/APIErrorHandler";
import { Success } from "../../../components/Notifications/Notifications";

export const AttachmentsContainer = ({ entryId, expandedAll, entryStatus, contentToReload }) => {

    const [data, query, totalSize, isLoading, handleTableChange, setReload, , blobData, viewAttachment] = useAttachments(null, entryId);
    const { t } = useTranslation();
    const [content, setContent] = useState(null);

    useEffect(() => {
        if (contentToReload?.contentName === "attachments")
            setReload(true);

    }, [contentToReload]);

    //#region Delete item with audit
    const handleRemove = (id) => {
        setContent(
            <ModalConfirm
                onConfirm={() => confirmRemoveItem(id)}
                onCancel={() => setContent(null)}
                title={t("medicalRecordsNotifications.item_delete_title")}
                description={t("medicalRecordsNotifications.item_delete_description")}
                confirmStyle="danger"
            />);
    }
    const confirmRemoveItem = (id) => {
        if (entryStatus === "Published" || entryStatus === "AutoPublished") {
            setContent(<CreateAuditModal
                onClose={() => setContent(null)}
                onSave={(reason, comments) => _removeItemAudited(id, reason, comments)}
            />)
        }
        else {
            _removeItemAudited(id, null, null);
        }
    }
    const _removeItemAudited = async (id, reason, comments) => {
        try {
            setContent(<ModalSpinner isShowing={true} hide={null} />);
            await removeAttachmentAPI(entryId, id, reason?.code, comments);
            setReload(true);
            setContent(null);
            Success("medicalRecordsNotifications.item_DeletedSuccessfully");
        }
        catch (error) {
            setContent(null);
            console.log(error);
            HandleApiError(error);
        }
    }
    //#endregion

    if (totalSize === 0)
        return null;

    return (
        <ExpandableContent title={t("medicalRecords.attachments.title")} expandedAll={expandedAll} content="attachments">
            <>
                {content}
                <DocumentViewer
                    file={blobData?.blob}
                    fileName={blobData?.fileName}
                    inModal={true}
                    userId={blobData?.metadata?.createdBy}
                    date={blobData?.metadata?.created}
                    isLoading={isLoading}
                />
                <Attachments
                    entryId={entryId}
                    withCard={false}
                    data={data}
                    query={query}
                    totalSize={totalSize}
                    isLoading={isLoading}
                    onTableChange={handleTableChange}
                    onDownloadFile={viewAttachment}
                    onRemove={handleRemove}
                />
            </>
        </ExpandableContent>

    );
}

export const AttachmentsCreateContainer = ({ entryId, hide, onSubmit }) => {

    const [data, , postAPI] = useFiles(uploadAttachmentEndpoint(entryId));
    const { t } = useTranslation();

    useEffect(() => {
        if (data.status === uploadStatusEnum.Uploaded)
            onSubmit("attachments");
    }, [data.status])

    const handleSubmit = async (values) => {
        try {
            await postAPI(values.attachments);
        }
        catch (error) {
            console.log("handleSubmit", error);
        }
    }

    const handleCancel = () => hide();

    if (data.isLoading || data.status === uploadStatusEnum.Uploading)
        return <Spinner />;

    return (
        <Formik
            onSubmit={handleSubmit}
        >
            {props => (
                <form onSubmit={props.handleSubmit}>
                    <FilesForm
                        placeholder={t('medicalRecords.attachments.upload')}
                        name={`attachments`}
                        onChange={props.setFieldValue}
                    />
                    <Row>
                        <Col md={12}>
                            <button type="button" className="btn btn-default" onClick={handleCancel}>{t("buttons.cancel")}</button>
                            <button type="submit" className="btn btn-success btn-right" >{t('buttons.save')}</button>
                        </Col>
                    </Row>
                </form>
            )}
        </Formik>
    );
}